import React, { Component } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { HorizontalBar } from 'react-chartjs-2'
import messages from 'components/Apps/SlaMonitor/translate'
import WidgetContainer from 'components/Apps/WidgetContainer'
import {
  fetchCageAvEData,
  fetchSiteAvEData,
  resetCageAVEData,
  setActiveCage,
} from 'reducers/slaMonitor/reducer'
import { getChartsRatio, getActiveLocus } from 'helpers/slaMonitor'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import CircleLoader from 'components/Loading/CircleLoader'
import { getFormattedNum } from 'helpers/math'

class SiteActualVsExpected extends Component {
  constructor(props) {
    super(props)
    this.state = {
      barIndex: null,
    }
    this.dataStructure = {
      index: null,
      id: 0,
      locus: {
        locus_id: 0,
        locus_name: 'N/A',
        difference: 0,
        feed_expected: 0,
        feed_used: 0,
      },
    }
  }

  componentDidMount() {
    this.props.fetchSiteAvEData({
      siteId: this.props.activeSite.id,
      date: this.props.activeDate.format('YYYY-MM-DD'),
    })
  }

  shouldComponentUpdate(nextProps) {
    const { siteAvEData, activeDate, activeSite, activeCage } = this.props

    if (
      activeDate !== nextProps.activeDate ||
      activeSite !== nextProps.activeSite
    ) {
      this.props.fetchSiteAvEData({
        siteId: nextProps.activeSite.id,
        date: nextProps.activeDate.format('YYYY-MM-DD'),
      })

      return false
    }

    if (
      activeCage !== nextProps.activeCage ||
      siteAvEData !== nextProps.siteAvEData
    ) {
      const locus = getActiveLocus(
        this.dataStructure,
        nextProps.activeCage,
        nextProps.siteAvEData,
      )

      this.setState(
        {
          barIndex: +locus.index,
        },
        () => {
          if (locus.id) {
            this.props.fetchCageAvEData({
              locusId: locus.id,
              date: nextProps.activeDate.format('YYYY-MM-DD'),
              locus: locus.locus,
            })
          } else {
            this.props.resetCageAVEData()
          }
        },
      )

      return false
    }

    return true
  }

  componentDidUpdate(prevProps) {
    const {
      siteAvEData,
      activeDate,
      fetchCageAvEData,
      resetCageAVEData,
    } = this.props
    const cages = siteAvEData.get('locus')
    const prevCages = prevProps.siteAvEData.get('locus')

    if (cages !== prevCages) {
      const { barIndex } = this.state

      if (cages !== undefined && Array.isArray(cages) && cages.length > 0) {
        fetchCageAvEData({
          locusId: cages[barIndex].locus_id,
          date: activeDate.format('YYYY-MM-DD'),
          locus: cages[barIndex],
        })
      } else {
        resetCageAVEData()
        this.setState({ barIndex: 0 })
      }
    }
  }

  /* eslint-disable no-nested-ternary */
  getColor = (v, s) => {
    if (v > 74)
      return !s
        ? 'rgba(115, 154, 73, 0.5)'
        : ifWindowWidthLessThan(992)
        ? 'rgba(115, 154, 73, 0.5)'
        : 'rgba(115, 154,73,1)'

    if (v > 25)
      return !s
        ? 'rgba(247, 194, 47, 0.5)'
        : ifWindowWidthLessThan(992)
        ? 'rgba(247, 194, 47, 0.5)'
        : 'rgba(247, 194, 47, 1)'

    return !s
      ? 'rgba(221, 89, 89, 0.5)'
      : ifWindowWidthLessThan(992)
      ? 'rgba(221, 89, 89, 0.5)'
      : 'rgba(221, 89, 89, 1)'
  }
  /* eslint-enable no-nested-ternary */

  getChartParams = () => {
    const cages = this.props.siteAvEData.get('locus')
    const { barIndex } = this.state
    const labels = []
    const colors = []
    const data = []
    const xTicks = { max: 0 }

    for (const key in cages) {
      if (Object.prototype.hasOwnProperty.call(cages, key)) {
        const cage = cages[key]
        const value = Math.round(cage.difference * 100)
        labels.push(cage.locus_name)
        data.push([0, value, Math.round(cage.feed_used)])
        colors.push(this.getColor(value, barIndex === +key))

        if (value > xTicks.max) {
          xTicks.max = value
        }
      }
    }

    xTicks.max = Math.ceil((xTicks.max + xTicks.max * 0.3) / 10) * 10

    return { labels, colors, data, xTicks }
  }

  chartData = ({ labels, colors, data }) => ({
    labels,
    datasets: [
      {
        backgroundColor: colors,
        data,
      },
    ],
  })

  chartOptions = ({ xTicks }) => {
    const {
      intl: { formatMessage, formatNumber },
    } = this.props

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.actualExpected)} (%)`,
            },
            ticks: xTicks,
          },
        ],
        yAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.cages),
            },
            ticks: {},
          },
        ],
      },
      tooltips: {
        enabled: false,
      },
      plugins: {
        datalabels: {
          anchor: 'end',
          align: 'end',
          color: 'black',
          font: {
            weight: 'bold',
            size: ifWindowWidthLessThan(1280) ? 9 : 11,
          },
          listeners: {
            click: this.onBarLabelClick,
          },
          formatter: v => `${v[1]}% (${formatNumber(v[2])})`,
        },
      },
    }
  }

  onBarLabelClick = chartEvent => {
    this.setHorizontalBarClick(chartEvent.dataIndex)
  }

  onHorizontalBarClick = chartEvent => {
    if (chartEvent.length) {
      this.setHorizontalBarClick(chartEvent[0]._index)
    }
  }

  setHorizontalBarClick = barIndex => {
    const locus = this.props.siteAvEData.get('locus')[barIndex]

    if (locus) {
      this.props.setActiveCage(locus.locus_id)

      if (ifWindowWidthLessThan(992)) {
        this.props.goToCage()
      }
    }
  }

  render() {
    const {
      hideRender,
      siteAvEData,
      chartLoader,
      activeSite,
      intl: { formatMessage },
    } = this.props

    if (hideRender === false) {
      return null
    }

    const infoData = siteAvEData.toJS()
    const chartParams = this.getChartParams()

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.actualExpected),
          end: activeSite.name,
        }}
        sideTitle={infoData.last_updated}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-info">
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(Math.round(infoData.total_feed_used))}
              </span>
              <span className="item-name">{`${formatMessage(
                messages.actual,
              )} (kg)`}</span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(Math.round(infoData.total_feed_expected))}
              </span>
              <span className="item-name">{`${formatMessage(
                messages.expected,
              )} (kg)`}</span>
            </div>
            <div className="info-item">
              <span className="item-count">{`${Math.round(
                infoData.total_difference * 100,
              )}%`}</span>
              <span className="item-name">
                {formatMessage(messages.actualExpected)}
              </span>
            </div>
          </div>
          <div className="chart-content">
            <CircleLoader loading={chartLoader} />
            <HorizontalBar
              width={null}
              height={null}
              data={this.chartData(chartParams)}
              options={this.chartOptions(chartParams)}
              getElementAtEvent={this.onHorizontalBarClick}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    siteAvEData: state.getIn(['slaMonitor', 'siteAvEData']),
    activeDate: state.getIn(['slaMonitor', 'activeDate']),
    activeSite: state.getIn(['profile', 'profile', 'site']),
    activeCage: state.getIn(['slaMonitor', 'activeCage']),
    chartLoader: state.getIn(['slaMonitor', 'siteAVELoader']),
  }),
  {
    fetchSiteAvEData,
    fetchCageAvEData,
    resetCageAVEData,
    setActiveCage,
  },
)(injectIntl(SiteActualVsExpected))
